import {
  Button,
  Col,
  Form,
  MonetaryInputField,
  RemovableCard,
  Row,
  SubmitButton,
  TextAreaInputField,
  TextInputField,
} from '@/components';
import { IndirectCostsFormSchema } from '@/features/project/projects/_models/payment';
import { useForm } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import type { sheetTypeEnum } from '@/utils/enum';
import { PlusOutlined } from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof IndirectCostsFormSchema>;

type Props = {
  sheetType: z.infer<typeof sheetTypeEnum>;
  initialValues: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const IndirectCostsForm: FC<Props> = ({
  sheetType,
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: {
      ...initialValues,
    },
    resolver: zodResolver(IndirectCostsFormSchema),
  });
  const {
    fields: paymentIndirectCostFields,
    append: appendPaymentIndirectCost,
    remove: removePaymentIndirectCost,
  } = useFieldArray({
    control,
    name: 'paymentIndirectCosts.costs',
  });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
      style={{ maxWidth: 480 }}
    >
      <Form.Item>
        <TextAreaInputField
          name="paymentIndirectCosts.label"
          label={t('indirect-cost-label')}
          hint={sheetType === 'RS' ? t('hint-indirect-cost-label') : undefined}
          control={control}
          errors={errors}
          rows={3}
        />

        <Row gutter={[0, 8]}>
          {paymentIndirectCostFields.map((item, idx) => {
            return (
              <Col key={item.id} span={24}>
                <RemovableCard
                  onRemove={() => {
                    removePaymentIndirectCost(idx);
                  }}
                >
                  <Row gutter={[8, 8]} wrap>
                    <Col span={12}>
                      <TextInputField
                        name={`paymentIndirectCosts.costs.${idx}.label`}
                        autocompleteKey="paymentIndirectCosts.cost.label"
                        label={t('indirect-cost-item-label')}
                        control={control}
                        errors={errors}
                      />
                    </Col>
                    <Col span={12}>
                      <MonetaryInputField
                        name={`paymentIndirectCosts.costs.${idx}.amount`}
                        label={t('indirect-cost-amount')}
                        control={control}
                        errors={errors}
                        unit={unit}
                      />
                    </Col>
                  </Row>
                </RemovableCard>
              </Col>
            );
          })}
          <Button
            type="dashed"
            onClick={() => {
              appendPaymentIndirectCost({
                label: null,
                amount: null,
              });
            }}
            block
            icon={<PlusOutlined />}
          >
            {t('add-indirect-cost')}
          </Button>
        </Row>
      </Form.Item>
      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
